/**
 * Knowledge Base API calls (documents + web sources for retrieval-augmented
 * search). Backed by gaussiq-intelligence's /api/knowledge/* routes.
 * Translates between the backend's snake_case rows and the camelCase shape
 * components use.
 *
 * A document is registered once and then holds one content-set per product
 * version — the unit that carries its own ingest status, page count and blob.
 * A document associates to zero or more products (the tenant's Software names);
 * only names already in the tenant's catalog can be linked, so the picker is
 * fed from fetchKnowledgeProducts rather than free text.
 */

import { authedJson } from './http'

const apiSend = (method, path, body) => authedJson(method, path, body)

// One product version's worth of a document's content.
function fromApiContentSet(row) {
  return {
    productVersion: row.product_version,
    status: row.status,
    pageCount: row.page_count ?? null,
    // The uploaded file's type ('PDF', 'DOCX', ...) — null for a web source's
    // content-set. Raw, unlike `meta`, so a caller that needs just the type
    // (not bundled with the page count) doesn't have to parse it back out.
    fileType: row.file_type ?? null,
    meta: row.meta ?? '',
    errorReason: row.error_reason ?? '',
    processedAt: row.processed_at ?? null,
  }
}

// Backend row -> camelCase shape used by the Knowledge components.
function fromApiSource(row) {
  return {
    id: row.knowledge_id,
    name: row.name,
    type: row.source_type,
    url: row.url ?? '',
    products: row.products ?? [],
    contentSets: (row.content_sets ?? []).map(fromApiContentSet),
    description: row.description ?? '',
    reviewDate: row.review_date ?? '',
    createdBy: row.created_by ?? null,
    updatedBy: row.updated_by ?? null,
    updatedAt: row.updated_at,
  }
}

/**
 * Fetch knowledge sources + summary stats, optionally filtered by type and by
 * a single associated product. Stats count documents, not content-sets.
 * POST /api/knowledge/list
 * @param {{ sourceType?: 'document'|'web', product?: string }} [filters]
 * @returns {Promise<{ sources: Array<object>, stats: { total, documents, web } }>}
 */
export async function fetchKnowledgeSources(filters = {}) {
  const data = await apiSend('POST', '/knowledge/list', {
    source_type: filters.sourceType,
    product: filters.product,
  })
  const stats = data.stats ?? {}
  return {
    sources: (data.sources ?? []).map(fromApiSource),
    stats: {
      total: stats.total ?? 0,
      documents: stats.documents ?? 0,
      web: stats.web ?? 0,
    },
  }
}

/**
 * The tenant's product names — the association picker's only valid options.
 * A name that isn't in this list is silently ignored by the backend.
 * GET /api/knowledge/products
 * @returns {Promise<string[]>}
 */
export async function fetchKnowledgeProducts() {
  const data = await apiSend('GET', '/knowledge/products')
  return data.products ?? []
}

/**
 * Register a brand-new document (file upload) or web source (URL) — the
 * register-new door. Fails with a 409 if the tenant already has a document
 * under this name; adding to an existing document goes through
 * addKnowledgeVersion instead.
 *
 * An empty productVersion means the content is version-agnostic; the backend
 * stores its sentinel for that case.
 *
 * For documents the response also carries a presigned S3 POST (`upload`) —
 * this call only registers metadata; pass `upload` + the file to
 * uploadDocumentFile to actually put the bytes in S3.
 * POST /api/knowledge/doc or POST /api/knowledge/url
 * @param {{ type: 'document'|'web', name: string, products?: string[], productVersion?: string, url?: string, description?: string, reviewDate?: string, file?: File }} payload
 * @returns {Promise<{ source: object, upload?: { url: string, fields: Record<string, string> } }>}
 */
export async function addKnowledgeSource(payload) {
  const path = payload.type === 'web' ? '/knowledge/url' : '/knowledge/doc'
  const data = await apiSend('POST', path, {
    name: payload.name,
    products: payload.products ?? [],
    product_version: payload.productVersion || undefined,
    ...(payload.type === 'web' && { url: payload.url }),
    // The extension is what the backend pins the presigned upload's Content-Type
    // to (see gaussiq_intelligence.core.knowledge_base.sources), so it needs the
    // real filename, not just the source's display name.
    ...(payload.type !== 'web' && { file_name: payload.file?.name }),
    description: payload.description,
    review_date: payload.reviewDate,
  })
  return { source: fromApiSource(data.source), upload: data.upload }
}

/**
 * Add a content-set to a document that already exists — the add-version door.
 * Re-using a product version resets that content-set and re-ingests it (the
 * worker skips the rewrite when the bytes are unchanged).
 * POST /api/knowledge/{sourceId}/versions
 * @param {number} sourceId
 * @param {{ productVersion?: string, file: File }} payload
 * @returns {Promise<{ contentSet: object, upload: { url: string, fields: Record<string, string> } }>}
 */
export async function addKnowledgeVersion(sourceId, payload = {}) {
  const data = await apiSend('POST', `/knowledge/${sourceId}/versions`, {
    product_version: payload.productVersion || undefined,
    // Same reasoning as addKnowledgeSource: the backend pins the presigned
    // upload's Content-Type from the extension, so it needs the real filename.
    file_name: payload.file?.name,
  })
  return { contentSet: fromApiContentSet(data.content_set), upload: data.upload }
}

/**
 * Upload a document's bytes straight to S3 using the presigned POST returned
 * by addKnowledgeSource/addKnowledgeVersion — the file never passes through our
 * API/ECS cluster. No Authorization header (S3 isn't behind our auth) and no
 * explicit Content-Type header on the request itself (the browser sets its
 * own multipart boundary); `upload.fields` already carries the per-field
 * Content-Type the presigned policy requires.
 * @param {{ url: string, fields: Record<string, string> }} upload
 * @param {File} file
 */
export async function uploadDocumentFile(upload, file) {
  const formData = new FormData()
  Object.entries(upload.fields).forEach(([key, value]) => formData.append(key, value))
  formData.append('file', file) // must come after the policy fields, per S3's presigned-POST contract

  const res = await fetch(upload.url, { method: 'POST', body: formData })
  if (!res.ok) {
    const err = new Error(`Failed to upload file to storage (status ${res.status})`)
    err.status = res.status
    throw err
  }
}

/**
 * Update a document's editable fields. These are all document-level — a
 * content-set's product version isn't editable (it's part of that content-set's
 * identity), and a web source's URL is locked in at creation.
 *
 * Only the fields you pass are touched; passing `products` replaces the whole
 * association set. The returned source carries no content-sets, so callers
 * should re-list rather than swap this into their state.
 * PATCH /api/knowledge/{sourceId}
 * A document's name is fixed at creation and is not sent here — it is baked into
 * every chunk's embedding at ingest time, so it can only be set when the document
 * (and its content) is first uploaded.
 * @param {number} sourceId
 * @param {{ products?: string[], description?: string, reviewDate?: string }} payload
 * @returns {Promise<{ source: object }>}
 */
export async function updateKnowledgeSource(sourceId, payload) {
  const data = await apiSend('PATCH', `/knowledge/${sourceId}`, {
    products: payload.products,
    description: payload.description,
    review_date: payload.reviewDate,
  })
  return { source: fromApiSource(data.source) }
}

/**
 * Delete one content-set — the primary delete unit. The document, its other
 * versions and its product links all survive; the last content-set going takes
 * the document with it. Soft: the backend flips the content-set to
 * "pending_delete" and a worker does the teardown.
 * DELETE /api/knowledge/{sourceId}/versions/{productVersion}
 * @param {number} sourceId
 * @param {string} productVersion — the raw stored value, sentinel included
 */
export async function deleteKnowledgeVersion(sourceId, productVersion) {
  return apiSend('DELETE', `/knowledge/${sourceId}/versions/${encodeURIComponent(productVersion)}`)
}

/**
 * Delete a whole document — every content-set at once (the aggregate). Soft,
 * same as deleteKnowledgeVersion. Rejected with a 409 while any content-set is
 * still processing.
 * DELETE /api/knowledge/{sourceId}
 * @param {number} sourceId
 */
export async function deleteKnowledgeSource(sourceId) {
  return apiSend('DELETE', `/knowledge/${sourceId}`)
}
